import {
  Content,
  Portal as RadixPortal,
  Trigger as RadixTrigger,
  Root,
} from "@radix-ui/react-popover";
import { CloseIcon } from "app/components/Icons";
import Input from "app/components/Input";
import { ticketDetailsLocales } from "app/containers/Features/containers/Ticket/locales";
import {
  formatEffortShorthand,
  parseEffortInput,
} from "app/containers/Features/helper";
import {
  EffortEstimatedPayload,
  EffortEstimatedType,
} from "app/containers/Features/types";
import clsx from "clsx";
import React, { FC, useEffect, useMemo, useState } from "react";

export const Header: FC<{
  assigneeId?: string;
  effortEstimated?: EffortEstimatedType[];
  onSubmit?: (payload: EffortEstimatedPayload) => void;
}> = ({ assigneeId, effortEstimated, onSubmit }) => {
  // Local States
  const [menuVisibility, setMenuVisibility] = useState<boolean>(false);
  const [effortInput, setEffortInput] = useState<string>("");

  // Functions
  const parsedEffort = parseEffortInput(effortInput);

  const handleSubmit = () => {
    if (parsedEffort.isValid) {
      const payload: EffortEstimatedPayload = {
        hours: parsedEffort.hours,
        minutes: parsedEffort.minutes,
        date: new Date().toISOString(),
      };

      if (onSubmit) {
        onSubmit(payload);
      }
      setMenuVisibility(false);
    }
  };

  // Variables
  const shouldOpenPopover = parsedEffort.isValid;
  const isPopoverOpen = menuVisibility && shouldOpenPopover;
  const currentEffortEstimated = useMemo(
    () => effortEstimated?.find((effort) => effort.memberId === assigneeId),
    [effortEstimated, assigneeId],
  );

  const formattedEffort = currentEffortEstimated
    ? formatEffortShorthand(
        currentEffortEstimated.effort?.hours,
        currentEffortEstimated.effort?.minutes,
      )
    : "-";

  // Effects
  useEffect(() => {
    setEffortInput(formattedEffort === "-" ? "" : formattedEffort);
  }, [formattedEffort]);

  return (
    <div className="flex items-center p-1 pr-2 pl-4 gap-2.5">
      <span className="text-sm text-black text-nowrap">
        {ticketDetailsLocales.effortsEstimated}
      </span>

      <Root
        open={isPopoverOpen}
        onOpenChange={(visible) => setMenuVisibility(visible)}
      >
        <RadixTrigger asChild>
          <div>
            <Input
              endIcon={
                <div
                  className={clsx(
                    "transition-opacity",
                    effortInput ? "opacity-100" : "opacity-0",
                  )}
                  onClick={() => setEffortInput("")}
                >
                  <CloseIcon size="20" />
                </div>
              }
              id={"time-estimation-input"}
              className="h-fit px-2.5 py-[13px] !text-sm !leading-none"
              inputClassName="!p-0 !h-fit !gap-0 !pr-2.5"
              placeholder={ticketDetailsLocales.enterTime}
              value={effortInput}
              onFocus={() => setMenuVisibility(true)}
              onChange={(e) => {
                const nextValue = e.target.value;
                setEffortInput(nextValue);
                setMenuVisibility(nextValue.trim().length > 0);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSubmit();
                }
              }}
            />
          </div>
        </RadixTrigger>

        <RadixPortal>
          <Content
            align="start"
            sideOffset={2}
            onOpenAutoFocus={(event) => event.preventDefault()}
            className="min-w-[var(--radix-popover-trigger-width)] max-w-full flex flex-col bg-white rounded border border-gray-100 shadow-[2px_2px_6px_0px_rgba(237,239,242,0.70)] z-50 py-2.5"
          >
            <p
              className="text-sm text-secondary px-3 py-2 hover:bg-gray-400 cursor-pointer"
              onClick={handleSubmit}
            >
              {parsedEffort.formatted}
            </p>
          </Content>
        </RadixPortal>
      </Root>
    </div>
  );
};
